import * as R from 'ramda';
import objectToFormData from 'object-to-formdata';
import { fetchApplication } from './index';
import * as actionTypes from '../constants/actionTypes';
import * as requestTypes from '../constants/requestTypes';
import setRequestInProcess from '../actions/request';
import api from '../utils/api';
import { successNotification, notInternalRespondErrorNotification, downloadResponseFile, errorNotification } from '../utils';

// Constants
const UPLOAD_STATUS_POLL_INTERVAL = 2000; // 2 seconds
const MAX_POLL_ATTEMPTS = 300; // 10 minutes maximum (300 * 2 seconds)


const COLUMN_CONFIGS = {
  PICK_COLUMNS: ['id'],
  OMIT_COLUMNS: ['key', 'valid', '_new', 'editable', 'meta', 'edidable_on_add']
};

const handleScheduleError = (response) => {
  if (response.errors.status == 400 && response.errors && response.errors.source && response.errors.source.params && response.errors.source.params.address) {
    errorNotification(response.errors.source.params.address);
  } else {
    notInternalRespondErrorNotification(response);
  }
};

const prepareProperty = (property, column, value, targetId, targetType) => {
  const columnsToPick = [...COLUMN_CONFIGS.PICK_COLUMNS, column];
  const prepared = property.id
    ? R.pick(columnsToPick, property)
    : R.omit(COLUMN_CONFIGS.OMIT_COLUMNS, property);

  return {
    ...prepared,
    [column]: value,
    target_id: targetId,
    target_type: targetType
  };
};

const processScheduleProperties = (properties) => {
  if (!properties || !Array.isArray(properties)) {
    return [];
  }

  return properties.map(property => {
    return Object.keys(property).reduce((acc, key) => {
      let value = property[key];

      if (value === '0%') {
        value = null;
      }
      if (key === 'rentAmount' && value === '0.0') {
        value = null;
      }

      acc[key] = value;
      return acc;
    }, {});
  });
};


const processScheduleCollection = (collection) => {
  if (!collection) {
    return collection;
  }

  return {
    ...collection,
    properties: processScheduleProperties(collection.properties)
  };
};


export const fetchSchedules = (appToken) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES;
  const url = `/applications/${appToken}/re_schedules`;

  dispatch(setRequestInProcess(true, requestType));
  return api
    .get(url)
    .then(({ data }) => {
      dispatch(setRequestInProcess(false, requestType));

      const processedCollection = processScheduleCollection(data.collection);

      dispatch(fetchApplicationSchedulesSuccess(processedCollection));
    })
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestType));
      notInternalRespondErrorNotification(response);
    });
};

export const fetchSchedulesDirectly = (appToken) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES;
  const url = `/applications/${appToken}/re_schedules`;

  dispatch(setRequestInProcess(true, requestType));
  return api
    .get(url)
    .then(({ data }) =>
      data
    )
    .catch((response) => {
      notInternalRespondErrorNotification(response);
    });
};

export const batchUpdateSchedules = (appToken, schedules, column, value, targetId, targetType) => (dispatch) => {
  const preparedProperties = schedules.map((property) => {
    const preparedProperty = { ...property };
    preparedProperty[column] = value;
    preparedProperty.target_id = targetId;
    preparedProperty.target_type = targetType;
    return preparedProperty;
  });

  const requestType = requestTypes.SCHEDULES;
  const url = `/applications/${appToken}/re_schedules/batch_update`;

  dispatch(setRequestInProcess(true, requestType));
  return api
    .post(url, { re_schedules: preparedProperties })
    .then(({ data }) => {
      dispatch(setRequestInProcess(false, requestType));
    })
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestType));
      handleScheduleError(response);
    });
};

export const fetchSchedulesFromExternal = (appToken, borrower = [], with_replace = false) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES;
  const url = `/applications/${appToken}/re_schedules/fetch`;

  dispatch(setRequestInProcess(true, requestType));
  return api
    .post(url, { borrower, with_replace })
    .then((response) => {
      const processedResponse = {
        ...response,
        data: {
          ...response.data,
          collection: processScheduleCollection(response.data.collection)
        }
      };

      dispatch(fetchApplicationSchedulesFromExternalSuccess(processedResponse));
      dispatch(setRequestInProcess(false, requestType));
    })
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestType));
      notInternalRespondErrorNotification(response);
    });
};

export const fetchSchedulesFromExternalDirectly = (appToken, borrower = [], with_replace = false) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES;
  const url = `/applications/${appToken}/re_schedules/fetch`;

  dispatch(setRequestInProcess(true, requestType));
  return api
    .post(url, { borrower, with_replace })
    .then(({ data }) => {
      const processedData = {
        ...data,
        collection: processScheduleCollection(data.collection)
      };

      return processedData;
    })
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestType));
      notInternalRespondErrorNotification(response);
    });
};

export const editApplicationScheduleRequest = (target, property, column, value, targetId, targetType, appToken) => (dispatch) => {
  const preparedProperty = prepareProperty(property, column, value, targetId, targetType);
  const isUpdate = Boolean(property.id);

  const url = isUpdate
    ? `/applications/${appToken}/re_schedules/${property.id}`
    : `/applications/${appToken}/re_schedules`;

  const method = isUpdate ? 'patch' : 'post';
  const payload = { re_schedule: preparedProperty };

  return api[method](url, payload)
    .then(({ data }) => {
      dispatch(editApplicationSchedule({
        property: data.reSchedule,
        key: property.key
      }));
      successNotification('Property was successfully updated');
    })
    .catch((response) => {
      handleScheduleError(response);
    });
};

export const removeApplicationScheduleRequest = (target, appToken, property) => (dispatch) => {
  if (!property.id) {
    dispatch(removeApplicationSchedule(property.key));
    return Promise.resolve();
  }

  const url = `/applications/${appToken}/re_schedules/${property.id}`;

  return api
    .delete(url)
    .then(({ data }) => {
      dispatch(removeApplicationSchedule(property.key));
      dispatch(fetchApplication(appToken));
      successNotification('Property was successfully removed');
    })
    .catch((response) => {
      handleScheduleError(response);
    });
};

export const downloadApplicationTemplate = (appToken) => (dispatch) => {
  const url = `/applications/${appToken}/re_schedules/template`;

  return api
    .get(url)
    .then((response) => {
      downloadResponseFile('application_template.xlsx', response);
      successNotification('Template downloaded successfully');
    })
    .catch((response) => {
      notInternalRespondErrorNotification(response);
    });
};


export const importApplicationScheduleRequest = (file, appToken, targetContactId) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES;
  const secondRequestType = requestTypes.SCHEDULES_IMPORT;
  const url = `/applications/${appToken}/re_schedules/import`;
  const formData = objectToFormData({ import: { asset: undefined } });
  formData.append('import[asset]', file);
  formData.append('import[target_contact_id]', targetContactId);

  dispatch(setRequestInProcess(true, requestType));
  dispatch(setRequestInProcess(true, requestType));

  return api
    .post(url, formData)
    .then(({ data }) => {
      dispatch(setRequestInProcess(false, requestType));
      dispatch(updateImportId(data.import.id));
      dispatch(fetchApplication(appToken));
      successNotification('Your file has been placed to the queue. Please reload this page in a couple of minutes.');
    })
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestType));
      notInternalRespondErrorNotification(response);
    });
};

export const uploadApplicationScheduleRequest = (appToken, file, withReplace, onProgress) => (dispatch) => {
  const requestTypeForImport = requestTypes.SCHEDULES_IMPORT;
  const url = `/applications/${appToken}/re_schedules/upload`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('with_replace', withReplace);

  dispatch(setRequestInProcess(true, requestTypeForImport));

  return api
    .post(url, formData)
    .then(({ data }) => {
      const fileKey = data.fileKey;
      return dispatch(checkApplicationScheduleUploadStatusRequest(fileKey, withReplace, appToken, onProgress));
    })
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestTypeForImport));
      notInternalRespondErrorNotification(response);
    });
};

export const checkApplicationScheduleUploadStatusRequest = (fileKey, withReplace, appToken, onProgress) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES_IMPORT;
  let pollAttempts = 0;
  let timeoutId = null;

  const checkStatus = () => {
    if (pollAttempts >= MAX_POLL_ATTEMPTS) {
      dispatch(setRequestInProcess(false, requestType));
      notInternalRespondErrorNotification('Upload timeout. Please try again.');
      return Promise.reject(new Error('Upload polling timeout'));
    }

    pollAttempts++;
    const url = `/applications/${appToken}/re_schedules/upload/status?file_key=${fileKey}&with_replace=${withReplace}`;

    return api
      .get(url)
      .then(({ data }) => {
        const { status, percentage } = data;
        if (onProgress) {
          onProgress(percentage !== 0 ? percentage : 10, status);
        }

        if (status === 'completed') {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          dispatch(setRequestInProcess(false, requestType));
          dispatch(fetchSchedules(appToken));
          successNotification('File processed successfully');
          return data.data;
        } else if (status === 'loading' || status === 'processing') {
          timeoutId = setTimeout(() => {
            checkStatus();
          }, UPLOAD_STATUS_POLL_INTERVAL);
          return null;
        } else {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          dispatch(setRequestInProcess(false, requestType));
          throw new Error(`Unexpected status: ${status}`);
        }
      })
      .catch((response) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        dispatch(setRequestInProcess(false, requestType));
        notInternalRespondErrorNotification('Upload failed. Please try again.');
        throw response;
      });
  };

  dispatch(setRequestInProcess(true, requestType));
  return checkStatus();
};

export const checkApplicationScheduleImportStatusRequest = (importId, appToken) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES_IMPORT;
  const url = `/applications/${appToken}/re_schedules/import/${importId}`;

  dispatch(setRequestInProcess(true, requestType));

  return api
    .get(url)
    .then(({ data }) => {
      dispatch(setRequestInProcess(false, requestType));
      dispatch(checkingImportStatus(data.import));
    })
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestType));
      notInternalRespondErrorNotification('Upload failed. Please try again.');
    });
};


export const addApplicationScheduleRequest = (target, property) => (dispatch, getState) => {
  const currentProperties = getState().schedules.properties;
  dispatch(addApplicationSchedule([...currentProperties, property]));
};

export const addApplicationSchedule = (data) => ({
  type: actionTypes.APPLICATION_SCHEDULES_ADD,
  payload: { data }
});

export const removeApplicationSchedule = (data) => ({
  type: actionTypes.APPLICATION_SCHEDULES_REMOVE,
  payload: { data }
});

export const editApplicationSchedule = (data) => ({
  type: actionTypes.APPLICATION_SCHEDULES_UPDATE,
  payload: { data }
});

export const fetchApplicationSchedulesSuccess = (payload) => ({
  type: actionTypes.APPLICATION_SCHEDULES_FETCH_SUCCESS,
  payload
});

export const fetchApplicationSchedulesFromExternalSuccess = (payload) => ({
  type: actionTypes.APPLICATION_SCHEDULES_FROM_EXTERNAL_FETCH_SUCCESS,
  payload
});

export const resetSchedules = () => ({
  type: actionTypes.APPLICATION_SCHEDULES_RESET
});

const updateImportId = (data) => ({
  type: actionTypes.APPLICATION_SCHEDULES_UPDATE_IMPORT_ID,
  payload: { data }
});

const checkingImportStatus = (data) => ({
  type: actionTypes.APPLICATION_SCHEDULES_CHECK_IMPORT_STATUS,
  payload: { data }
});
